import { lazy } from "react";
import { Navigate, Route } from "react-router";
import { CREATE, MODIFY, VIEW } from "src/context/FoodpilotContext";
import { AppParamsSettings } from "../AppParams/AppParam";
import { ApplicationCreate } from "../Applications/ApplicationCreate";
import { ApplicationEdit } from "../Applications/ApplicationEdit";
import { BrandCreate } from "../Brand/Create";
import { BrandEdit } from "../Brand/Edit";
import { BrandList } from "../Brand/List";
import { Campaigns } from "../Campaigns";
import { CompaniesList } from "../Company/CompaniesList";
import { Company } from "../Company/Company";
import { CompanyCreate } from "../Company/CompanyCreate";
import { CompanyEdit } from "../Company/CompanyEdit";
import { StrategyOverview } from "../CsrStrategy/StrategyOverview";
import { CsrThemeCreate } from "../CsrTheme/Create";
import { CsrThemeEdit } from "../CsrTheme/Edit";
import { CsrTheme } from "../CsrTheme/List";
import { CustomThemesList } from "../CustomTheme/CustomThemesList";
import { DataInvalidation } from "../DataInvalidation/DataInvalidation";
import { DataListsValuesOverrides } from "../DataListsValuesOverrides/DataListsValuesOverrides";
import EntityTargetScoreAssignment from "../EntityTargetScoreAssignment";
import { DynaBlockEditor } from "../Extensions/DynaBlockEditor";
import { DynaBlockManager } from "../Extensions/DynaBlockManager";
import { DynaBlocksProtection } from "./DynaBlocksProtection";
import { FeatureSwitchProtection } from "./FeatureSwitchProtection";
import { APP_INTEGRATION_FEATURE_SWITCH } from "src/api/client-api/applications/constants";
import { CONSENT_GOVERNANCE_FEATURE_SWITCH } from "src/api/client-api/consent/constants";
import FeatureActivation from "../FeatureSwitch/FeatureActivation";
import { IcvList } from "../ICVs/IcvList";
import { IngredientLineCreate } from "../IngredientLine/Create";
import { IngredientLineEdit } from "../IngredientLine/Edit";
import { IngredientLineList } from "../IngredientLine/List";
import { IngredientReferenceList } from "../IngredientReferences/IngredientReferenceList";
import Integrations from "../Integrations";
import { JsonAttributes } from "../JsonAttributes";
import { LanguageManagement } from "../Languages/LanguageManagement";
import { MenuManagement } from "../MenuManagement/MenuManagement";
import { NewsSettings } from "../NewsSettings";
import { OrganizationTypeList } from "../OrganizationTypeList";
import { PackagingsSsq } from "../Packagings/Ssq";
import PermissionList from "../Permissions/PermissionList";
import { ProductLineCreate } from "../ProductLine/ProductLineCreate";
import { ProductLineEdit } from "../ProductLine/ProductLineEdit";
import { ProductLineList } from "../ProductLineList";
import { QuestionnairesList } from "../Questionnaires/QuestionnairesList";
import { Questionnaire } from "../Questionnaires/Questionnaire";
import { RoleCreate } from "../Roles/Create";
import { RoleEdit } from "../Roles/Edit";
import { RolesList } from "../Roles/List";
import { ScoresDisplayed } from "../ScoresDisplayed/ScoresDisplayed";
import { ProvidersList } from "../Supplier/ProvidersList";
import { TagOverview } from "../TagManagement/TagOverview";
import { TagTypeCreate } from "../TagManagement/TagTypeCreate";
import { TagTypeEdit } from "../TagManagement/TagTypeEdit";
import { TagTypesList } from "../TagManagement/TagTypesList";
import { TagValueCreate } from "../TagManagement/TagValueCreate";
import { TagValueEdit } from "../TagManagement/TagValueEdit";
import { TagValuesList } from "../TagManagement/TagValuesList";
import ThemePage from "../Theme/ThemePage";
import { TranslationManagement } from "../Translations";
import { UsersList } from "../Users/UsersList";
import { CMSPagesManager } from "../CMS/Pages/CMSPagesManager";
import { CMSTemplatesManager } from "../CMS/Pages/CMSTemplatesManager";
import { CMSRouteContext } from "./CMSRouteContext";
import { SettingsRouteBoundary } from "./SettingsRouteBoundary";
import { OrganizationAdministrationProtection } from "./OrganizationAdministrationProtection";
import { StrategySettingsContext } from "./StrategySettingsContext";
import { URLHandler } from "src/services/URLHandler";
import { CampaignsProtection } from "./CampaignsProtection";
import { CMSPagesProtection } from "./CMSPagesProtection";
import { BrandsProtection } from "./BrandsProtection";
import { NavigationMenuProtection } from "./NavigationMenuProtection";
import { RolesProtection } from "./RolesProtection";
import { UsersProtection } from "./UsersProtection";
import { PackagingSsqProtection } from "./PackagingSsqProtection";
import { LocaleManagementProtection } from "./LocaleManagementProtection";
import { PermissionsProtection } from "./PermissionsProtection";
import { PlatformAdminProtection } from "./PlatformAdminProtection";
import { ScoresProtection } from "./ScoresProtection";
import { CustomThemeProtection } from "./CustomThemeProtection";
import { ApplicationListLazy, CompanySectorListLazy } from "./lazySettingsPages";

const CMSPageEditorLazy = lazy(() =>
    import("../CMS/Pages/CMSPageEditor").then((module) => ({ default: module.CMSPageEditor })),
);
const CustomThemeEditorLazy = lazy(() =>
    import("../CustomTheme/CustomThemeEditor").then((module) => ({ default: module.CustomThemeEditor })),
);

export const legacyRoutes = () => (
    <>
        <Route element={<PlatformAdminProtection />}>
            <Route path="features" element={<FeatureActivation />} />
            <Route element={<FeatureSwitchProtection featureSwitch={CONSENT_GOVERNANCE_FEATURE_SWITCH} />}>
                <Route
                    path="consent-governance"
                    element={<Navigate replace to={URLHandler.settings.consentGovernance()} />}
                />
                <Route
                    path="my-organization/consent-governance"
                    element={<Navigate replace to={URLHandler.settings.consentGovernance()} />}
                />
            </Route>
        </Route>
        <Route path="media-library" element={<Navigate replace to={URLHandler.settings.mediaLibrary.root()} />} />
        <Route element={<ScoresProtection />}>
            <Route path="scores" element={<ScoresDisplayed />} />
        </Route>
        <Route element={<CampaignsProtection />}>
            <Route path="campaigns" element={<Campaigns />} />
        </Route>
        <Route path="product-line">
            <Route index element={<ProductLineList />} />
            <Route path="create" element={<ProductLineCreate />} />
            <Route path=":id/edit" element={<ProductLineEdit />} />
        </Route>
        <Route
            path="company-sectors"
            element={
                <SettingsRouteBoundary>
                    <CompanySectorListLazy />
                </SettingsRouteBoundary>
            }
        />
        <Route path="organization-types" element={<OrganizationTypeList />} />
        <Route path="tags">
            <Route index element={<TagOverview />} />
            <Route path="types">
                <Route index element={<TagTypesList />} />
                <Route path="create" element={<TagTypeCreate />} />
                <Route path=":tagTypeId/edit" element={<TagTypeEdit />} />
            </Route>
            <Route path="values">
                <Route index element={<TagValuesList />} />
                <Route path="create" element={<TagValueCreate />} />
                <Route path=":tagValueId/edit" element={<TagValueEdit />} />
            </Route>
        </Route>
        <Route path="newsfeed" element={<NewsSettings />} />
        <Route element={<PlatformAdminProtection />}>
            <Route path="csrTheme">
                <Route index element={<CsrTheme />} />
                <Route path="create" element={<CsrThemeCreate />} />
                <Route path=":csrThemeId/edit" element={<CsrThemeEdit />} />
            </Route>
        </Route>
        <Route path="companies">
            <Route
                index
                element={
                    <OrganizationAdministrationProtection action={VIEW}>
                        <CompaniesList isProvider={false} />
                    </OrganizationAdministrationProtection>
                }
            />
            <Route
                path=":id"
                element={
                    <OrganizationAdministrationProtection action={VIEW}>
                        <Company />
                    </OrganizationAdministrationProtection>
                }
            />
            <Route
                path="create"
                element={
                    <OrganizationAdministrationProtection action={CREATE}>
                        <CompanyCreate isProvider={false} />
                    </OrganizationAdministrationProtection>
                }
            />
            <Route
                path=":id/edit"
                element={
                    <OrganizationAdministrationProtection action={MODIFY}>
                        <CompanyEdit isProvider={false} />
                    </OrganizationAdministrationProtection>
                }
            />
        </Route>
        <Route path="providers">
            <Route
                index
                element={
                    <OrganizationAdministrationProtection action={VIEW}>
                        <ProvidersList isProvider={true} />
                    </OrganizationAdministrationProtection>
                }
            />
            <Route
                path="create"
                element={
                    <OrganizationAdministrationProtection action={CREATE}>
                        <CompanyCreate isProvider={true} />
                    </OrganizationAdministrationProtection>
                }
            />
            <Route
                path=":id/edit"
                element={
                    <OrganizationAdministrationProtection action={MODIFY}>
                        <CompanyEdit isProvider={true} />
                    </OrganizationAdministrationProtection>
                }
            />
        </Route>
        <Route element={<UsersProtection />}>
            <Route path="users" element={<UsersList />} />
        </Route>
        <Route element={<PlatformAdminProtection />}>
            <Route path="applicationParameters" element={<AppParamsSettings />} />
        </Route>
        <Route element={<BrandsProtection />}>
            <Route path="brands">
                <Route index element={<BrandList />} />
                <Route element={<BrandsProtection action={CREATE} />}>
                    <Route path="create" element={<BrandCreate />} />
                </Route>
                <Route element={<BrandsProtection action={MODIFY} />}>
                    <Route path=":brandId/edit" element={<BrandEdit />} />
                </Route>
            </Route>
        </Route>
        <Route path="ingredient-line">
            <Route index element={<IngredientLineList />} />
            <Route path="create" element={<IngredientLineCreate />} />
            <Route path=":ingredientLineId/edit" element={<IngredientLineEdit />} />
        </Route>
        <Route path="packagings">
            <Route element={<PackagingSsqProtection />}>
                <Route path="ssq" element={<PackagingsSsq />} />
            </Route>
        </Route>
        <Route element={<PermissionsProtection />}>
            <Route path="permissions">
                <Route index element={<PermissionList />} />
            </Route>
        </Route>
        <Route path="roles">
            <Route element={<RolesProtection action={VIEW} />}>
                <Route index element={<RolesList />} />
            </Route>
            <Route element={<RolesProtection action={CREATE} />}>
                <Route path="create" element={<RoleCreate />} />
            </Route>
            <Route element={<RolesProtection action={MODIFY} />}>
                <Route path=":roleCode/edit" element={<RoleEdit />} />
            </Route>
        </Route>
        <Route element={<PlatformAdminProtection />}>
            <Route element={<FeatureSwitchProtection featureSwitch={APP_INTEGRATION_FEATURE_SWITCH} />}>
                <Route path="applications">
                    <Route
                        index
                        element={
                            <SettingsRouteBoundary>
                                <ApplicationListLazy />
                            </SettingsRouteBoundary>
                        }
                    />
                    <Route path=":id/edit" element={<ApplicationEdit />} />
                    <Route path="create" element={<ApplicationCreate />} />
                </Route>
            </Route>
        </Route>
        <Route path="entity-target-score-assignment" element={<EntityTargetScoreAssignment />} />
        <Route path="data-lists-values-overrides" element={<DataListsValuesOverrides />} />
        <Route path="additional-object-parameters" element={<JsonAttributes />} />
        <Route element={<PlatformAdminProtection />}>
            <Route path="integrations" element={<Integrations />} />
            <Route path="translations">
                <Route index element={<TranslationManagement />} />
            </Route>
            <Route path="theme" element={<ThemePage />} />
        </Route>
        <Route element={<CustomThemeProtection action={VIEW} />}>
            <Route path="custom-themes">
                <Route index element={<CustomThemesList />} />
            </Route>
            <Route element={<CustomThemeProtection action={CREATE} />}>
                <Route
                    path="custom-theme"
                    element={
                        <SettingsRouteBoundary>
                            <CustomThemeEditorLazy />
                        </SettingsRouteBoundary>
                    }
                />
            </Route>
            <Route element={<CustomThemeProtection action={MODIFY} />}>
                <Route
                    path="custom-theme/edit/:themeId"
                    element={
                        <SettingsRouteBoundary>
                            <CustomThemeEditorLazy />
                        </SettingsRouteBoundary>
                    }
                />
            </Route>
        </Route>
        <Route element={<LocaleManagementProtection />}>
            <Route path="languages">
                <Route index element={<LanguageManagement />} />
            </Route>
        </Route>
        <Route element={<NavigationMenuProtection />}>
            <Route path="menu-management" element={<MenuManagement />} />
        </Route>
        <Route path="cms">
            <Route
                index
                element={
                    <CMSPagesProtection>
                        <CMSPagesManager />
                    </CMSPagesProtection>
                }
            />
            <Route element={<DynaBlocksProtection action={VIEW} />}>
                <Route path="dynablocks" element={<DynaBlockManager />} />
                <Route element={<DynaBlocksProtection action={MODIFY} />}>
                    <Route path="dynablocks/:extensionId" element={<DynaBlockEditor />} />
                </Route>
            </Route>
            <Route
                path="templates"
                element={
                    <CMSPagesProtection>
                        <CMSTemplatesManager />
                    </CMSPagesProtection>
                }
            />
            <Route element={<CMSRouteContext />}>
                <Route
                    path=":slug"
                    element={
                        <SettingsRouteBoundary>
                            <CMSPageEditorLazy />
                        </SettingsRouteBoundary>
                    }
                />
            </Route>
        </Route>
        <Route path="icvs" element={<IcvList />} />
        <Route path="ingredient-references" element={<IngredientReferenceList />} />
        <Route path="csrStrategy" element={<StrategySettingsContext />}>
            <Route index element={<Navigate to="general" replace />} />
            <Route path="general" element={<StrategyOverview />} />
            <Route path="pillars" element={<Navigate to="../general" replace />} />
            <Route path="pillars/new" element={<Navigate to={URLHandler.settings.csrStrategy.general()} replace />} />
            <Route
                path="pillars/:pillarId"
                element={<Navigate to={URLHandler.settings.csrStrategy.general()} replace />}
            />
        </Route>
        <Route path="questionnaires">
            <Route index element={<QuestionnairesList />} />
            <Route path=":id" element={<Questionnaire />} />
        </Route>
        <Route path="data-invalidation" element={<DataInvalidation />} />
    </>
);
